import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Clock3, Trophy } from "lucide-react";
import Lottie from "lottie-react";
import {
  PreassessmentQuestion,
  AssessmentQuestion,
  AssessmentAnswerResponse,
} from "../../../../axios/quiz";
import { AssessmentStreakBadge } from "../../assessment-streak-badge/assessment-streak-badge";
import { AssessmentInteractiveQuestion } from "../../../interactive-questions/assessment-interactive-question";
import type { AssessmentInteractiveSubmitGetterRef } from "../../../interactive-questions/assessment-mode";
import { AssessmentScoreFeedback } from "../../assessment-score-feedback/assessment-score-feedback";
import { AssessmentEvaluatingOverlay } from "../../assessment-evaluating-overlay/assessment-evaluating-overlay";
import { AssessmentOpenTextField } from "../../assessment-open-text-field/assessment-open-text-field";
import { isInteractiveAssessmentType, isMultipleChoiceAnswers, getAssessmentQuestionType, isOpenTextAssessmentType } from "../../../../utils/assessmentInteractive";
import {
  containerStyle,
  questionHeaderStyle,
  questionTextStyle,
  questionAreaStyle,
  dividerStyle,
  answerAreaLayoutStyle,
  answerAreaContentStyle,
  answerAreaSidebarStyle,
  formControlStyle,
  postAnswerMetaStyle,
  questionWinnerIconStyle,
  questionWinnerMetaStyle,
  questionWinnerSeparatorStyle,
  questionWinnerStyle,
  questionWinnerTextStyle,
  radioStyle,
  getOptionStyle,
} from "./styles";

import goodAnimation5Mob from "../../../../assets/lottie/good-main-pre-assessment-mobile.json";
import goodAnimation4Mob from "../../../../assets/lottie/good2-main-pre-assessment-mobile.json";
import normalAnimation3Mob from "../../../../assets/lottie/normal-main-pre-assessment-mobile.json";
import badAnimation1Mob from "../../../../assets/lottie/bad-main-pre-assessment-mobile.json";
import badAnimation2Mob from "../../../../assets/lottie/bad2-main-pre-assessment-mobile.json";
import goodAnimation4Desk from "../../../../assets/lottie/good-main-pre-assessment-desktop.json";
import goodAnimation5Desk from "../../../../assets/lottie/good2-main-pre-assessment-desktop.json";
import normalAnimation3Desk from "../../../../assets/lottie/normal-main-pre-assessment-desktop.json";
import badAnimation1Desk from "../../../../assets/lottie/bad-main-pre-assessment-desktop.json";
import badAnimation2Desk from "../../../../assets/lottie/bad2-main-pre-assessment-desktop.json";

interface QuizMainBlockProps {
  question: PreassessmentQuestion | AssessmentQuestion;
  questionIndex: number;
  totalQuestions: number;
  selectedAnswer: string;
  onAnswerSelect: (answer: string) => void;
  isAnswerChecked: boolean;
  answerResponse?: AssessmentAnswerResponse | null;
  isSubmitting?: boolean;
  sidebar?: React.ReactNode;
  questionBadge?: React.ReactNode;
  hideQuestionHeader?: boolean;
  hideQuestionWinner?: boolean;
  hideStreak?: boolean;
  hideAnswerPoints?: boolean;
  revealAnswerFeedback?: boolean;
  allowAnswerEditing?: boolean;
  openTextFocusRequestKey?: number;
  openTextValidationError?: string | null;
  openTextLocked?: boolean;
  isAnswerFinalized?: boolean;
  assessmentId?: string | null;
  assessmentQuestionIndex?: number | null;
  interactiveSubmitGetterRef?: AssessmentInteractiveSubmitGetterRef;
  onInteractiveCompletionChange?: (isComplete: boolean) => void;
  isInteractiveAnswerChecked?: boolean;
  interactiveCheckedState?: {
    userAnswers?: Record<string, string>;
    userOrder?: string[];
    userPairs?: Array<{ left_id: string; right_id: string }>;
    isCorrect?: boolean;
    feedback?: string;
  };
}

export const QuizMainBlock: React.FC<QuizMainBlockProps> = ({
  question,
  questionIndex,
  totalQuestions,
  selectedAnswer,
  onAnswerSelect,
  isAnswerChecked,
  answerResponse,
  isSubmitting = false,
  sidebar,
  questionBadge = null,
  hideQuestionHeader = false,
  hideQuestionWinner = false,
  hideStreak = false,
  hideAnswerPoints = false,
  revealAnswerFeedback = true,
  allowAnswerEditing = false,
  openTextFocusRequestKey = 0,
  openTextValidationError = null,
  openTextLocked = false,
  isAnswerFinalized = false,
  assessmentId = null,
  assessmentQuestionIndex = null,
  interactiveSubmitGetterRef,
  onInteractiveCompletionChange,
  isInteractiveAnswerChecked = false,
  interactiveCheckedState,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [lottieFinished, setLottieFinished] = useState(false);

  useEffect(() => {
    setLottieFinished(false);
  }, [questionIndex]);

  const questionType = getAssessmentQuestionType(question as AssessmentQuestion);
  const isOpenText = isOpenTextAssessmentType(questionType);
  const isInteractive = isInteractiveAssessmentType(questionType);
  const lockForAfterAnswerFeedback =
    revealAnswerFeedback &&
    (isAnswerChecked || isInteractiveAnswerChecked);
  const answersLocked =
    (isAnswerChecked && !allowAnswerEditing) ||
    isAnswerFinalized ||
    lockForAfterAnswerFeedback;
  const openTextInputLocked = openTextLocked || answersLocked;
  const interactiveAnswersLocked =
    (isInteractiveAnswerChecked && !allowAnswerEditing) ||
    isAnswerFinalized ||
    (revealAnswerFeedback && isInteractiveAnswerChecked);

  const questionMcAnswers = isMultipleChoiceAnswers(question.answers)
    ? question.answers
    : null;
  const responseMcAnswers = isMultipleChoiceAnswers(answerResponse?.answers)
    ? answerResponse.answers
    : null;
  // Prefer points from assessment_answer response when backend withholds them
  // from the initial question payload.
  const mcAnswers =
    questionMcAnswers && responseMcAnswers
      ? questionMcAnswers.map((ans) => {
          const matched = responseMcAnswers.find(
            (responseAns) => responseAns.text === ans.text,
          );
          return matched
            ? { ...ans, points: matched.points }
            : ans;
        })
      : (responseMcAnswers ?? questionMcAnswers);
  const interactiveQuestion =
    isInteractive &&
    revealAnswerFeedback &&
    answerResponse?.answers !== undefined
      ? ({
          ...(question as AssessmentQuestion),
          answers: answerResponse.answers,
        } as AssessmentQuestion)
      : (question as AssessmentQuestion);
  const selectedAnswerData =
    !isOpenText && !isInteractive && mcAnswers
      ? (mcAnswers.find((ans) => ans.text === selectedAnswer) ?? null)
      : null;
  const points =
    (isOpenText || isInteractive) && answerResponse
      ? (answerResponse.score ?? null)
      : selectedAnswerData && typeof selectedAnswerData.points === "number"
        ? selectedAnswerData.points
        : typeof answerResponse?.score === "number"
          ? answerResponse.score
          : null;

  const animationMapMobile: Record<number, any> = {
    0: badAnimation1Mob,
    1: badAnimation1Mob,
    2: badAnimation2Mob,
    3: normalAnimation3Mob,
    4: goodAnimation4Mob,
    5: goodAnimation5Mob,
  };

  const animationMapDesktop: Record<number, any> = {
    0: badAnimation1Desk,
    1: badAnimation1Desk,
    2: badAnimation2Desk,
    3: normalAnimation3Desk,
    4: goodAnimation4Desk,
    5: goodAnimation5Desk,
  };

  const getAnimationData = () => {
    if (points === null) return null;
    const key = Math.max(0, Math.min(points, 5));
    return isMobile ? animationMapMobile[key] : animationMapDesktop[key];
  };

  const formattedWinnerTime =
    answerResponse?.question_winner?.time_spent !== undefined
      ? answerResponse.question_winner.time_spent.toFixed(1).replace(/\.0$/, "")
      : null;

  const hasSidebar = Boolean(sidebar);
  const showAnswerFeedback = revealAnswerFeedback && isAnswerChecked;
  const showQuestionWinner =
    !hideQuestionWinner && Boolean(answerResponse?.question_winner);
  const showStreak =
    !hideStreak &&
    isAnswerChecked &&
    typeof answerResponse?.streak === "number" &&
    answerResponse.streak > 0;
  const optionCheckedForStyle = showAnswerFeedback;
  const displayPoints = showAnswerFeedback ? points : null;

  return (
    <Box sx={{ ...containerStyle, position: "relative" }}>
      <Box sx={questionAreaStyle(hasSidebar)}>
        {!hideQuestionHeader ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="subtitle1" sx={questionHeaderStyle}>
              Question {questionIndex} of {totalQuestions}
            </Typography>
            {questionBadge}
          </Box>
        ) : null}

        {!isInteractive ? (
          <>
            <Typography variant="body1" sx={questionTextStyle}>
              {question.question}
            </Typography>

            <Divider sx={dividerStyle} />
          </>
        ) : null}
      </Box>

      <Box sx={answerAreaLayoutStyle(hasSidebar, isOpenText || isInteractive)}>
        <Box sx={answerAreaContentStyle}>
          {isInteractive ? (
            <>
              <AssessmentEvaluatingOverlay
                active={isSubmitting}
                evaluateMultiple={allowAnswerEditing}
              >
                <AssessmentInteractiveQuestion
                  question={interactiveQuestion}
                  isAnswerChecked={interactiveAnswersLocked}
                  checkedState={
                    revealAnswerFeedback && answerResponse
                      ? {
                          ...interactiveCheckedState,
                          feedback: answerResponse.justification,
                          isCorrect:
                            typeof answerResponse.score === "number"
                              ? answerResponse.score > 0
                              : interactiveCheckedState?.isCorrect,
                        }
                      : interactiveCheckedState
                  }
                  hideSubmitButton
                  hideFeedback
                  hideResultHighlight={!revealAnswerFeedback}
                  submitGetterRef={interactiveSubmitGetterRef}
                  onCompletionChange={onInteractiveCompletionChange}
                />
              </AssessmentEvaluatingOverlay>
              {showAnswerFeedback &&
              answerResponse &&
              typeof answerResponse.score === "number" ? (
                <AssessmentScoreFeedback
                  score={answerResponse.score}
                  bonusScore={answerResponse.bonus_score}
                  justification={answerResponse.justification}
                />
              ) : null}
            </>
          ) : isOpenText ? (
            <Box>
              <AssessmentEvaluatingOverlay
                active={isSubmitting}
                evaluateMultiple={allowAnswerEditing}
              >
                <AssessmentOpenTextField
                  key={questionIndex}
                  value={selectedAnswer}
                  onChange={onAnswerSelect}
                  disabled={openTextInputLocked || isSubmitting}
                  isAnswerChecked={isAnswerChecked}
                  points={displayPoints}
                  focusRequestKey={openTextFocusRequestKey}
                  errorMessage={openTextValidationError}
                  assessmentId={assessmentId}
                  questionIndex={assessmentQuestionIndex}
                />
              </AssessmentEvaluatingOverlay>
              {showAnswerFeedback &&
              answerResponse &&
              typeof answerResponse.score === "number" ? (
                <AssessmentScoreFeedback
                  score={answerResponse.score}
                  bonusScore={answerResponse.bonus_score}
                  justification={answerResponse.justification}
                />
              ) : null}
            </Box>
          ) : (
            <AssessmentEvaluatingOverlay
              active={isSubmitting}
              evaluateMultiple={allowAnswerEditing}
            >
              <FormControl
                component="fieldset"
                fullWidth
                disabled={answersLocked || isSubmitting}
                sx={formControlStyle}
              >
                <RadioGroup
                  name={`quiz-answers-${questionIndex}`}
                  value={selectedAnswer}
                  onChange={(e) => onAnswerSelect(e.target.value)}
                >
                  {mcAnswers
                    ? mcAnswers.map((ans) => {
                        const optionPoints =
                          typeof ans.points === "number" ? ans.points : null;
                        return (
                    <Box
                      key={ans.text}
                      sx={{
                        mb: 1,
                        position: "relative",
                      }}
                    >
                      <FormControlLabel
                        value={ans.text}
                        control={<Radio sx={radioStyle} />}
                        label={
                          optionCheckedForStyle ? (
                            <span>
                              {ans.text}
                              {hideAnswerPoints || optionPoints === null ? null : (
                                <>
                                  {" ("}
                                  <span
                                    style={{ fontWeight: "bold", fontStyle: "italic" }}
                                  >
                                    {optionPoints > 0 ? "+" : ""}
                                    {optionPoints} Points
                                  </span>
                                  {")"}
                                </>
                              )}
                            </span>
                          ) : (
                            ans.text
                          )
                        }
                        labelPlacement="start"
                        sx={{
                          ...getOptionStyle(
                            ans.text,
                            optionPoints ?? 0,
                            selectedAnswer,
                            optionCheckedForStyle && optionPoints !== null,
                            theme
                          ),
                          margin: 0,
                          width: "100%",
                        }}
                      />
                    </Box>
                        );
                      })
                    : null}
                </RadioGroup>
              </FormControl>
            </AssessmentEvaluatingOverlay>
          )}
        </Box>
        {sidebar ? <Box sx={answerAreaSidebarStyle}>{sidebar}</Box> : null}
      </Box>

      {isAnswerChecked && (showQuestionWinner || showStreak) ? (
        <Box sx={postAnswerMetaStyle}>
          {showQuestionWinner && answerResponse?.question_winner ? (
            <Box sx={questionWinnerStyle}>
              <Box sx={questionWinnerIconStyle}>
                <Trophy size={16} />
              </Box>
              <Typography sx={questionWinnerTextStyle}>
                {`Question Winner: ${answerResponse.question_winner.name}`}
              </Typography>
              {formattedWinnerTime ? (
                <>
                  <Box sx={questionWinnerSeparatorStyle} />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <Clock3 size={14} />
                    <Typography sx={questionWinnerMetaStyle}>
                      {`Fastest correct answer: ${formattedWinnerTime} sec`}
                    </Typography>
                  </Box>
                </>
              ) : null}
            </Box>
          ) : null}
          {showStreak ? (
            <AssessmentStreakBadge streakCount={answerResponse?.streak ?? 0} />
          ) : null}
        </Box>
      ) : null}

      {showAnswerFeedback && !lottieFinished && getAnimationData() && (
        <Box
          sx={{
            position: "fixed",
            top: "-15%",
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            pointerEvents: "none",
            overflow: "hidden",
            objectFit: "cover",
          }}
        >
          <Lottie
            animationData={getAnimationData()}
            loop={false}
            onComplete={() => setLottieFinished(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default QuizMainBlock;
